{
  "blocks": [],
  "relationships": [],
  "localeVariants": [
    {
      "id": "new-article-preparing-for-art",
      "locale": "fr",
      "variant": {
        "title": "Se préparer à la PMA",
        "subtitle": "Bien démarrer une FIV, une IIU ou une vitrification d'ovocytes",
        "body": {
          "introduction": "Se lancer dans un parcours de PMA, c'est une étape importante. Que tu partes vers une IIU, une FIV ou une vitrification d'ovocytes, « se préparer » ne se résume pas à honorer les rendez-vous, les prises de sang et les injections. Il y a plusieurs terrains sur lesquels tu peux travailler en amont pour arriver au traitement plus informée, mieux organisée et mieux entourée.",
          "quickSummary": [
            {
              "title": "Fais le point médical.",
              "description": "Bilan de santé général, traitements en cours, vaccins et dépistage d'infections avant le début du protocole."
            },
            {
              "title": "L'hygiène de vie joue.",
              "description": "Arrêter de fumer, réduire l'alcool, ajuster le poids et améliorer le sommeil peuvent améliorer les résultats et ton vécu du traitement."
            },
            {
              "title": "La prépa émotionnelle compte.",
              "description": "Organise le soutien en amont, pose des limites claires, et envisage un accompagnement psy si tu vis avec de l'anxiété, une dépression ou un traumatisme passé."
            }
          ],
          "sections": [
            {
              "title": "Faire le point médical",
              "subtitle": "Avant le démarrage",
              "content": "Un bon bilan pré-PMA balaie plusieurs sujets. Demande à ton médecin : « Y a-t-il des points de santé qu'on devrait stabiliser avant de commencer ? » et « Ai-je besoin de vaccins ou d'un dépistage d'infections avant le traitement ? »",
              "bulletPoints": [
                "Santé générale : tension, IMC, pathologies chroniques (thyroïde, diabète, hypertension, etc.).",
                "Traitements en cours : certains médicaments (humeur, tension, acné, épilepsie…) peuvent nécessiter un ajustement avant une grossesse. N'arrête rien seule. Demande au prescripteur ce qui est compatible avec la PMA.",
                "Vaccinations : les médecins vérifient souvent l'immunité contre la rubéole, la varicelle et parfois l'hépatite avant le traitement pour mettre à jour ce qu'il faut.",
                "Dépistage d'infections : tests IST et bilans sanguins (VIH, hépatites, syphilis) sont standards dans beaucoup de cliniques avant une PMA."
              ]
            },
            {
              "title": "Poser des bases côté hygiène de vie",
              "subtitle": "Soutenir le traitement",
              "content": "Les changements d'hygiène de vie ne « réparent » pas tout, mais ils peuvent faire pencher la balance en ta faveur et améliorer ton vécu de la PMA. Pas besoin de devenir un robot du bien-être : concentre-toi sur 1 ou 2 changements réalistes sur quelques mois.",
              "bulletPoints": [
                "Tabac / vape : arrêter est l'un des gestes les plus puissants pour la fertilité. Le tabac est lié à une moins bonne qualité d'ovocytes et de spermatozoïdes, à des taux de succès de FIV plus bas et à un risque de fausse couche plus élevé.",
                "Alcool : une consommation fréquente ou importante est associée à une fertilité réduite. Beaucoup de cliniques recommandent de limiter l'alcool pendant la préparation et de l'arrêter complètement pendant la stimulation.",
                "Poids et activité : un IMC très bas ou très élevé peut compliquer la conception. Une activité douce et régulière et une alimentation équilibrée durent mieux dans le temps qu'un régime choc.",
                "Sommeil et stress : mal dormir et vivre un stress chronique perturbent l'équilibre hormonal et le vécu du traitement. Des routines simples (heures de lever fixes, exercices de relaxation) aident."
              ]
            },
            {
              "title": "Se préparer émotionnellement",
              "subtitle": "Ça compte vraiment",
              "content": "La PMA, ce n'est presque jamais « juste un acte médical ». Elle touche à l'identité, aux relations, à l'argent, aux attentes de la famille… c'est normal que ça bouscule.",
              "bulletPoints": [
                "Décide ce que tu veux savoir (ou pas). Certaines personnes se sentent mieux avec toutes les statistiques ; d'autres préfèrent une vue d'ensemble.",
                "Organise le soutien en amont. Y a-t-il un·e proche qui peut venir aux rendez-vous clés ? Ta clinique propose-t-elle un accompagnement psy ou un groupe de parole ?",
                "Pose des limites claires : ce que tu veux partager (ou pas) avec la famille, les collègues, les réseaux sociaux. C'est OK de garder certaines choses privées.",
                "Si tu vis déjà avec de l'anxiété, une dépression, un traumatisme passé ou des douleurs chroniques, prends un temps avec un·e thérapeute ou un·e professionnel·le de la santé mentale avant de commencer."
              ]
            },
            {
              "title": "Maîtriser le côté pratique",
              "subtitle": "Argent, temps, logistique",
              "content": "Anticiper évite la panique de dernière minute et réduit le stress pendant le traitement.",
              "bulletPoints": [
                "Argent et prise en charge : clarifie ce que couvrent ton assurance ou la sécurité sociale. Vérifie si ta clinique ou ton employeur proposent des solutions de financement.",
                "Travail et planning : demande la fréquence des rendez-vous sur un cycle type et les jours à poser autour de la ponction ou du transfert.",
                "Pense au transport, à la garde d'enfants et à la flexibilité de ton poste."
              ]
            },
            {
              "title": "Parler avec ton/ta partenaire",
              "subtitle": "Si tu en as un·e",
              "content": "Si tu fais une PMA en couple, vous êtes tous les deux patients, même si un seul reçoit les injections. En solo, tu peux t'appuyer sur un·e « allié·e fertilité » de confiance pour partager la charge émotionnelle et logistique.",
              "bulletPoints": [
                "Jusqu'où vous êtes prêts à aller : combien de cycles ? Recours au don de sperme, d'ovocytes, d'embryons ? Adoption ? Une vie sans enfant si ça ne marche pas ?",
                "Comment répartir la charge : qui gère l'administratif ? Qui fait les injections ? Qui conduit après les gestes ?",
                "Sexe et intimité : la PMA peut transformer la sexualité en un rendez-vous médical. En parler tôt aide à protéger votre couple."
              ]
            },
            {
              "title": "Convenir de l'étape suivante",
              "subtitle": "Après ce cycle",
              "content": "Avant de démarrer, c'est utile de demander : « Si ce cycle ne marche pas, que recommanderais-tu ensuite ? » Tu n'as pas à t'engager tout de suite sur cette étape, mais savoir qu'il y a un plan rend un résultat négatif un peu moins vécu comme une impasse.",
              "bulletPoints": [
                "Refaire le même protocole.",
                "Modifier la stimulation ou passer d'une IIU à une FIV.",
                "Ajouter ou retirer certaines options en labo (ICSI, DPI, etc.).",
                "Envisager un don de gamètes ou une GPA.",
                "Faire une pause."
              ]
            }
          ]
        }
      }
    },
    {
      "id": "new-article-egg-retrieval-day",
      "locale": "fr",
      "variant": {
        "title": "Le jour de la ponction",
        "subtitle": "Comment te préparer, ce qui se passe, comment récupérer",
        "body": {
          "introduction": "La ponction ovocytaire (aussi appelée prélèvement d'ovocytes) est un geste court, mais elle peut avoir un côté « grand jour » : c'est la ligne d'arrivée de la stimulation et le début de la suite. Cet article t'accompagne sur la préparation, le déroulé du jour J et la récupération.",
          "quickSummary": [
            {
              "title": "Le timing du déclenchement est crucial.",
              "description": "L'injection de déclenchement, environ 36 heures avant la ponction, permet aux ovocytes de terminer leur maturation. Si tu la rates, ne devine pas : appelle la clinique."
            },
            {
              "title": "Prévois la récup.",
              "description": "Organise ton retour à la maison et prévois de ne pas conduire pendant au moins 24 h. La plupart des personnes se sentent « en règles » plutôt que « post-op »."
            },
            {
              "title": "Repère les signes d'alerte.",
              "description": "Douleur sévère, ventre très gonflé, fièvre, ou une quantité d'urine nettement réduite justifient un appel urgent à ta clinique."
            }
          ],
          "sections": [
            {
              "title": "Ce qu'est la ponction",
              "subtitle": "Expliquée simplement",
              "content": "Pendant une FIV (ou une vitrification d'ovocytes), les ovocytes sont prélevés à l'aide d'une fine aiguille guidée par une échographie vaginale. Le médecin aspire doucement le liquide de chaque follicule ; l'équipe de biologie cherche ensuite les ovocytes dans ce liquide.",
              "bulletPoints": [
                "Le geste est en général rapide (souvent autour de 20 minutes) et se fait typiquement sous sédation.",
                "Tous les follicules ne contiennent pas un ovocyte, et le nombre récupéré peut être inférieur au nombre de follicules vus à l'écho."
              ]
            },
            {
              "title": "Comment te préparer",
              "subtitle": "Avant la ponction",
              "content": "La préparation est simple mais importante. Suis à la lettre les consignes de ta clinique.",
              "bulletPoints": [
                "Déclenchement : traite-le comme une mission critique. On te dira l'heure exacte : la maturation des ovocytes dépend du timing.",
                "Jeûne : la plupart des cliniques imposent de ne rien manger ni boire pendant plusieurs heures avant, à cause de la sédation.",
                "Le matin : douche et pas de maquillage, ni parfum, ni vernis, ni bijoux, ni lentilles de contact.",
                "Organise ton retour : la sédation altère le jugement et la coordination. Certaines cliniques recommandent une voiture ou un taxi plutôt que les transports en commun.",
                "Prévois de ne pas conduire ni manipuler de machines pendant au moins 24 heures."
              ]
            },
            {
              "title": "Ce qui se passe le jour J",
              "subtitle": "Le déroulé",
              "content": "Une fois enregistrée, tu enfiles une blouse et on te pose une petite perfusion pour la sédation.",
              "bulletPoints": [
                "Une sonde d'échographie est introduite dans le vagin ; une aiguille fine fixée dessus prélève le liquide folliculaire de chaque ovaire.",
                "Tu es surveillée en continu pendant le geste.",
                "Ensuite, tu te reposes en salle de récupération environ 1 à 2 heures avant de rentrer."
              ]
            },
            {
              "title": "À quoi t'attendre après",
              "subtitle": "Les symptômes de récupération",
              "content": "La plupart des personnes se sentent « en règles » plutôt que « post-op ». Symptômes fréquents et normaux :",
              "bulletPoints": [
                "Fatigue et légères crampes ou gêne pelvienne.",
                "Petits saignements vaginaux ou perte teintée de sang (là où l'aiguille est passée).",
                "Le paracétamol est souvent recommandé pour la douleur.",
                "Utilise des serviettes, pas de tampons, pour limiter le risque d'infection.",
                "Prévois une journée calme : traite les premières 24 heures comme un temps de récupération."
              ]
            },
            {
              "title": "Quand appeler ta clinique",
              "subtitle": "Les signes d'alerte",
              "content": "Appelle ta clinique (ou les urgences) si tu as des symptômes qui dépassent ce qui était annoncé :",
              "bulletPoints": [
                "Nausées ou vomissements persistants.",
                "Douleur abdominale sévère ou qui s'aggrave.",
                "Ventre très gonflé ou tendu.",
                "Une quantité d'urine nettement inférieure à d'habitude.",
                "Fièvre ou sensation de mal-être général.",
                "Ces signes peuvent annoncer un syndrome d'hyperstimulation ovarienne (SHO), qui peut s'aggraver en cas de grossesse."
              ]
            }
          ]
        }
      }
    },
    {
      "id": "new-article-bloating-after-retrieval",
      "locale": "fr",
      "variant": {
        "title": "Le ventre gonflé après la ponction",
        "subtitle": "Pourquoi ça arrive et ce que tu peux faire",
        "body": {
          "introduction": "La ponction est une étape clé d'un cycle de FIV ou de vitrification d'ovocytes. Pendant le geste, le médecin prélève tes ovocytes matures avec une fine aiguille. C'est en général rapide et sûr, mais beaucoup ressentent ensuite un ventre gonflé, lourd, « plein ». Ça peut être inconfortable et un peu inquiétant si tu ne t'y attendais pas.",
          "quickSummary": [
            {
              "title": "C'est très fréquent.",
              "description": "Le ballonnement après la ponction est lié à des ovaires agrandis, à une rétention d'eau passagère et à une irritation pelvienne légère. Chez la plupart, ça s'améliore en une semaine."
            },
            {
              "title": "Des gestes simples aident.",
              "description": "Marche douce, hydratation, aliments faciles à digérer et antalgique validé par ta clinique peuvent vraiment améliorer le confort."
            },
            {
              "title": "Sache quand appeler.",
              "description": "Douleur sévère, prise de poids rapide, très peu d'urine, essoufflement : appelle ta clinique en urgence."
            }
          ],
          "sections": [
            {
              "title": "Pourquoi j'ai le ventre si gonflé ?",
              "subtitle": "Comprendre les causes",
              "content": "Après la ponction, le ballonnement est très fréquent. La plupart du temps, c'est la combinaison de plusieurs facteurs.",
              "bulletPoints": [
                "Des ovaires stimulés et agrandis : avant la ponction, les hormones font grossir plusieurs follicules à la fois, ce qui rend les ovaires temporairement plus gros et appuie sur les organes voisins.",
                "Mouvements de liquides et rétention d'eau : un taux d'œstrogènes élevé fait retenir plus d'eau, perturbe l'équilibre électrolytique et ralentit la digestion.",
                "Irritation légère après le geste : l'aiguille traverse la paroi vaginale jusqu'à chaque ovaire, laissant de petits points d'irritation qui se résorbent seuls.",
                "Chez certaines personnes qui répondent très fort à la stimulation, le ballonnement peut être un signe précoce de syndrome d'hyperstimulation ovarienne (SHO)."
              ]
            },
            {
              "title": "Combien de temps ça dure ?",
              "subtitle": "À quoi t'attendre",
              "content": "Ça varie beaucoup d'une personne à l'autre et dépend du nombre de follicules, du taux d'œstrogènes atteint et du risque de SHO.",
              "bulletPoints": [
                "Chez beaucoup, ballonnement et gêne culminent dans les 2 à 3 premiers jours après la ponction.",
                "Puis ça s'améliore progressivement sur environ une semaine, à mesure que les hormones redescendent et que les ovaires reprennent leur taille.",
                "Si tu es enceinte au même cycle, les symptômes peuvent durer plus longtemps parce que les hormones de grossesse continuent de stimuler les ovaires."
              ]
            },
            {
              "title": "Bouger doucement",
              "subtitle": "Relancer la machine",
              "content": "De petites marches à la maison ou à l'extérieur stimulent la digestion, réduisent les gaz et aident les liquides à circuler.",
              "bulletPoints": [
                "Évite les sports d'impact et le port de charges tant que ta clinique ne t'a pas dit que c'est bon.",
                "Tes ovaires sont encore agrandis et plus fragiles juste après la ponction."
              ]
            },
            {
              "title": "Hydratation et alimentation",
              "subtitle": "Soulager la digestion",
              "content": "Vise une hydratation régulière sur la journée (souvent ~1,5 à 2 L sauf indication contraire). Eau, tisanes simples (gingembre, menthe) et bouillons clairs sont de bonnes options.",
              "bulletPoints": [
                "Attention aux tisanes « détox » ou fortement laxatives : elles peuvent provoquer diarrhée ou déshydratation et ne sont pas recommandées en cas de grossesse possible.",
                "Privilégie les aliments cuits, faciles à digérer : légumes cuits, riz, avoine, protéines maigres.",
                "Limite les aliments qui font gonfler : grosses portions de légumineuses, crucifères (chou, brocoli, chou-fleur), plats très gras ou ultra-transformés.",
                "Si tu es sensible au lait de vache, essaie une alternative sans lactose ou végétale."
              ]
            },
            {
              "title": "Confort et repos",
              "subtitle": "Soutenir la récup",
              "content": "Des gestes simples de confort font une vraie différence, surtout les 24 à 48 premières heures.",
              "bulletPoints": [
                "Des vêtements souples et amples à la taille améliorent nettement le confort.",
                "Repose-toi quand tu en as besoin, et si ta clinique le valide, une bouillotte tiède (pas brûlante) enveloppée dans un tissu peut aider.",
                "Beaucoup de cliniques recommandent le paracétamol. Suis toujours l'avis de ta clinique sur les médicaments autorisés."
              ]
            },
            {
              "title": "Quand appeler ta clinique",
              "subtitle": "Les signes d'alerte",
              "content": "Un ballonnement seul est banal, mais contacte rapidement ta clinique (ou consulte en urgence) si tu remarques les signes suivants. Ils peuvent évoquer un SHO modéré à sévère ou une autre complication.",
              "bulletPoints": [
                "Ventre très gonflé et dur, qui grossit visiblement d'un jour à l'autre.",
                "Douleur sévère qui ne cède pas au repos ni à l'antalgique recommandé.",
                "Essoufflement, gêne à la poitrine, ou impression de ne plus pouvoir t'allonger à plat.",
                "Nausées et vomissements qui t'empêchent de boire ou de manger.",
                "Très peu d'urine (moins que d'habitude, urine très foncée).",
                "Prise de poids rapide en 24-48 h (par exemple plus de 2 à 3 kg).",
                "Vertiges, malaise, sensation de mal-être marquée."
              ]
            }
          ]
        }
      }
    },
    {
      "id": "new-article-egg-freezing-prep",
      "locale": "fr",
      "variant": {
        "title": "Se préparer à vitrifier ses ovocytes",
        "subtitle": "Préparation émotionnelle, médicale et financière",
        "body": {
          "introduction": "Vitrifier ses ovocytes, c'est un grand geste de soin envers soi. Ça peut aussi paraître… beaucoup. Il y a la décision, les hormones, la logistique, le coût, et tous les « et si ». Cet article est là pour t'aider à te préparer, pas à te convaincre. Considère-le comme un complément au Guide de la vitrification d'ovocytes : le guide explique ce que c'est, celui-ci parle du comment se préparer.",
          "quickSummary": [
            {
              "title": "Nomme ton « pourquoi ».",
              "description": "Comprendre ta raison personnelle t'ancre quand le processus devient lourd."
            },
            {
              "title": "Connais les étapes.",
              "description": "Stimulation, rendez-vous de suivi, ponction : visualiser le déroulé rend le tout moins effrayant."
            },
            {
              "title": "Anticipe les coûts.",
              "description": "Cycle, médicaments et stockage annuel s'additionnent. Connaître les chiffres évite les mauvaises surprises."
            }
          ],
          "sections": [
            {
              "title": "Préparation émotionnelle",
              "subtitle": "Faire de la place à tes émotions",
              "content": "On arrive rarement à la vitrification depuis un endroit neutre. Il y a souvent une histoire derrière : un job exigeant, une rupture, des soucis de santé, l'absence d'un·e partenaire, ou juste le sentiment que le temps file plus vite qu'on ne voudrait.",
              "bulletPoints": [
                "« Je ne suis pas prête à avoir un enfant maintenant, mais je ne veux pas que ce choix me soit retiré plus tard »",
                "« Je démarre un traitement qui pourrait affecter ma fertilité, je veux une sécurité »",
                "« Mon chemin vers la parentalité est incertain, c'est une façon de réduire la pression »",
                "C'est normal si tes émotions ne s'alignent pas : tu peux te sentir fière et proactive tout en étant triste ou apeurée.",
                "Pense à en parler à un·e thérapeute connaissant les enjeux fertilité, à une personne de confiance ou à d'autres personnes qui ont vitrifié leurs ovocytes."
              ]
            },
            {
              "title": "Préparation médicale",
              "subtitle": "Visualiser le parcours",
              "content": "De l'extérieur, la vitrification impressionne : hormones, échographies, bloc opératoire, étapes de laboratoire que tu ne verras jamais. Tu n'as pas besoin de devenir experte, mais visualiser le déroulé rend en général le tout moins effrayant.",
              "bulletPoints": [
                "La plupart des cliniques commencent par un état des lieux : âge, régularité du cycle, taux hormonaux et échographie des ovaires.",
                "Pose des questions directes : « À quoi ressemblerait un résultat réaliste sur un cycle ? » « Un seul cycle suffira-t-il ou en faudra-t-il plusieurs ? »",
                "La phase de stimulation dure environ 10 à 14 jours, avec injections d'hormones et rendez-vous de suivi.",
                "Pense pratique : flexibilité au travail pour les rendez-vous du matin, quelqu'un pour t'accompagner le jour de la ponction, éviter les gros engagements pendant le cycle.",
                "La ponction est un geste court sous sédation. Tu peux être fatiguée, avoir des crampes ou le ventre gonflé pendant deux ou trois jours après."
              ]
            },
            {
              "title": "Préparation financière",
              "subtitle": "Rendre les chiffres moins effrayants",
              "content": "Le coût est souvent l'une des parts les plus stressantes, surtout parce que ce n'est pas un seul chiffre. C'est une addition de plusieurs postes qui s'étalent dans le temps.",
              "bulletPoints": [
                "Le cycle lui-même : consultations, suivi, ponction et vitrification.",
                "Les médicaments de stimulation peuvent parfois doubler la facture.",
                "Les frais de stockage chaque année où les ovocytes restent congelés.",
                "L'utilisation future des ovocytes (décongélation, fécondation, transfert) a ses propres coûts, proches d'une FIV.",
                "Explore : ton assurance, ton pays ou ton employeur proposent-ils une prise en charge ou un avantage fertilité ? Existe-t-il des facilités de paiement ou des aides ?"
              ]
            },
            {
              "title": "Aller à ton rythme",
              "subtitle": "Le droit d'être humaine",
              "content": "Se préparer à la vitrification, ce n'est pas un examen que tu passes ou rates. C'est un processus : rassembler l'information, écouter ton corps et tes émotions, voir ce qui a du sens pour toi maintenant.",
              "bulletPoints": [
                "Tu as le droit de changer d'avis.",
                "Tu as le droit de reposer deux fois la même question.",
                "Tu as le droit de te sentir bien un jour et débordée le lendemain.",
                "Tu as le droit de décider d'attendre, ou de te sentir prête plus tôt que prévu.",
                "Prendre le temps de penser les aspects émotionnel, médical et financier, c'est prendre soin de ta fertilité et de la personne que tu seras demain."
              ]
            }
          ]
        }
      }
    },
    {
      "id": "new-article-sperm-freezing-guide",
      "locale": "fr",
      "variant": {
        "title": "Congeler son sperme",
        "subtitle": "Pourquoi, quand et comment ça marche",
        "body": {
          "introduction": "Si avoir des enfants un jour est une possibilité (même si tu ne sais pas quand), la congélation de sperme te laisse des options. Tu stockes un échantillon maintenant, au cas où la vie prendrait un tour différent ou que la fertilité deviendrait plus compliquée que prévu. Vois ça comme une photo de ta fertilité d'aujourd'hui, gardée au frais.",
          "quickSummary": [
            {
              "title": "Un plan B, pas un traitement.",
              "description": "Congeler ne change pas ta fertilité d'aujourd'hui. Ça met un échantillon de côté pour une utilisation future en IIU, FIV ou ICSI."
            },
            {
              "title": "Les raisons fréquentes.",
              "description": "Flexibilité, avant un traitement qui peut toucher la fertilité (chimio, radiothérapie, testostérone, vasectomie), ou parce que la qualité du sperme peut évoluer avec l'âge et l'hygiène de vie."
            },
            {
              "title": "La qualité tient dans le temps.",
              "description": "Des naissances ont été rapportées après plusieurs décennies de conservation, et les résultats congelé vs frais en FIV/ICSI sont souvent comparables."
            }
          ],
          "sections": [
            {
              "title": "Ce qu'est (et n'est pas) la congélation de sperme",
              "subtitle": "Un instantané, pas un traitement",
              "content": "La congélation de sperme (aussi appelée cryoconservation) consiste à recueillir un échantillon, à le préparer en laboratoire et à le stocker à très basse température dans de l'azote liquide. C'est ce qui le garde stable sur le long terme.",
              "bulletPoints": [
                "Congeler ne modifie pas ta fertilité d'aujourd'hui : c'est du stockage, pas un traitement.",
                "Vois ça comme une photo de ton potentiel actuel de fertilité.",
                "Les échantillons congelés peuvent servir plus tard via IIU, FIV ou ICSI selon la situation."
              ]
            },
            {
              "title": "Pourquoi les gens congèlent",
              "subtitle": "Flexibilité, raisons médicales, sécurité face au temps",
              "content": "Il y a rarement une seule raison. Certains veulent de la flexibilité, d'autres ont une raison médicale claire, d'autres encore préfèrent simplement se couvrir face au fait que la fertilité n'est pas figée.",
              "bulletPoints": [
                "Flexibilité : timing de carrière, voyages, absence du bon partenaire, tranquillité d'esprit.",
                "Avant un traitement médical : chimiothérapie, radiothérapie ou traitement à la testostérone peuvent affecter la production de sperme, parfois durablement.",
                "Avant une chirurgie : la vasectomie est le cas classique. Mettre des échantillons de côté avant garde ouverte la possibilité d'enfants biologiques plus tard.",
                "Parce que la fertilité peut évoluer : âge, tabac, alcool en quantité, obésité, sédentarité et certaines expositions environnementales sont liés à une qualité de sperme réduite.",
                "La production de sperme est continue : il faut environ 2 à 3 mois pour fabriquer un « nouveau lot », donc changer d'hygiène de vie peut compter si tu prévois aussi de concevoir."
              ]
            },
            {
              "title": "Le déroulé, étape par étape",
              "subtitle": "Recueil, labo, stockage",
              "content": "Le processus est simple. En voir la forme le rend moins abstrait.",
              "bulletPoints": [
                "Tu donnes un échantillon. La plupart des cliniques recueillent sur place dans une salle privée. Certaines autorisent un recueil à domicile si tu peux le déposer rapidement. Les règles varient.",
                "On te demandera en général d'attendre quelques jours sans éjaculer avant (souvent 2 à 5 jours). La clinique te dira quoi faire.",
                "Beaucoup de personnes déposent 2 à 3 échantillons sur des visites séparées de quelques jours. Ça crée une marge de sécurité et donne plus de doses utilisables plus tard.",
                "Le labo traite l'échantillon et le congèle. Il est réparti en plusieurs paillettes, ce qui te laisse plusieurs tentatives possibles.",
                "Certaines cliniques testent une petite portion en la décongelant (« test-thaw ») pour prévoir comment l'échantillon se comportera après congélation.",
                "Le stockage est de longue durée. Des grossesses et naissances ont été rapportées après des décennies dans l'azote liquide."
              ]
            },
            {
              "title": "Ce que ça coûte (ordre de grandeur US)",
              "subtitle": "C'est un forfait, pas un chiffre unique",
              "content": "Les prix varient beaucoup selon la clinique, la ville et ce qui est inclus : consultation, dépistages infectieux, spermogramme, durée de stockage, et frais futurs de retrait ou d'envoi.",
              "bulletPoints": [
                "La congélation initiale plus la première année de stockage vont typiquement de quelques centaines à quelques milliers de dollars, surtout quand tout est bundlé avec les tests obligatoires et les frais de dossier.",
                "Le stockage ensuite est facturé mensuellement ou annuellement.",
                "L'assurance couvre rarement une congélation « choisie ». La prise en charge est plus fréquente avant un traitement gonadotoxique. Ça vaut la peine de demander.",
                "Quand tu compares des devis, demande : « Qu'est-ce qui est exactement inclus ? » (tests, nombre de paillettes, première année de stockage, frais futurs de retrait et d'envoi)."
              ]
            },
            {
              "title": "Quand tu veux t'en servir",
              "subtitle": "Du stockage au chemin qui s'ouvre",
              "content": "Le moment venu, la clinique décongèle une paillette et te propose l'option la plus simple adaptée à ta situation.",
              "bulletPoints": [
                "IIU (insémination) : possible si l'échantillon décongelé est suffisamment mobile.",
                "FIV : peut être recommandée s'il y a d'autres facteurs de fertilité en jeu.",
                "ICSI : une forme de FIV utilisée quand la concentration ou la mobilité est basse. Elle peut fonctionner avec très peu de spermatozoïdes mobiles.",
                "Avoir du sperme congelé ne t'oblige pas à passer par la FIV/ICSI, mais ça rend ces options possibles si tu en as besoin un jour."
              ]
            },
            {
              "title": "Démêler les idées reçues",
              "subtitle": "Les inquiétudes courantes, en bref",
              "content": "Quelques questions qui reviennent souvent, avec la version courte de la réponse.",
              "bulletPoints": [
                "La congélation abîme-t-elle le sperme ? Elle réduit typiquement la mobilité, mais beaucoup d'échantillons restent utilisables avec les techniques de labo modernes.",
                "Le congelé, c'est moins bien que le frais ? Pas forcément. En FIV/ICSI, les résultats peuvent être comparables selon le contexte clinique.",
                "Est-ce que ça affectera la santé d'un futur enfant ? La congélation de sperme est utilisée depuis des décennies et les données de suivi sont rassurantes. La recherche continue d'évoluer, comme dans toute la fertilité.",
                "Je perds de la fertilité si je congèle ? Non. Congeler ne t'enlève pas de fertilité : c'est du stockage pour plus tard."
              ]
            }
          ]
        }
      }
    },
    {
      "id": "new-article-life-after-preservation",
      "locale": "fr",
      "variant": {
        "title": "La vie après la préservation",
        "subtitle": "Ce qui se passe maintenant que tout est au stockage",
        "body": {
          "introduction": "Congeler ovocytes, sperme ou embryons est une étape marquante. Une fois les rendez-vous, injections et gestes derrière soi, on s'attend souvent au soulagement… puis une nouvelle question arrive : et maintenant que tout est au stockage, il se passe quoi ?",
          "quickSummary": [
            {
              "title": "Le stockage met la biologie en pause.",
              "description": "Les échantillons dans l'azote liquide sont en animation suspendue, sûrs pendant des années tant que le site maintient les bonnes conditions."
            },
            {
              "title": "Les frais demandent un suivi actif.",
              "description": "Les coûts annuels varient beaucoup selon la clinique et la ville. Traite le stockage comme un abonnement que tu suis activement."
            },
            {
              "title": "L'administratif te protège.",
              "description": "Consentements et choix de devenir des échantillons comptent plus qu'on ne le croit, surtout pour les embryons."
            }
          ],
          "sections": [
            {
              "title": "Ce que veut dire « stockage »",
              "subtitle": "Azote liquide et sécurité long terme",
              "content": "Quand ovocytes, sperme ou embryons sont congelés, ils sont conservés dans des cuves cryogéniques remplies d'azote liquide. À ces températures, l'activité biologique est essentiellement mise en pause. C'est pour ça que le stockage peut durer plusieurs années tant que le site maintient les bonnes conditions et une surveillance rigoureuse.\n\nOn stocke en général dans l'un des deux lieux. Il n'y a pas de « meilleur » universel : ce qui compte le plus, c'est la sécurité, la traçabilité, et un plan clair pour la facturation et les consentements.",
              "bulletPoints": [
                "🏥 Le labo de ta clinique de fertilité, fréquent si tu as congelé lors d'une FIV ou d'une vitrification sur place.",
                "🏢 Un site de stockage long terme dédié, parfois moins cher, parfois choisi pour la logistique, parfois recommandé par les cliniques."
              ]
            },
            {
              "title": "Frais annuels de stockage et ce qu'ils couvrent",
              "subtitle": "Coûts, facturation, extras cachés",
              "content": "La tarification n'est pas standardisée aux États-Unis. Elle varie selon la clinique, la ville et ce que tu stockes. La plupart des sites facturent à l'année (le plus courant), au mois, ou proposent des remises sur plusieurs années. Ordres de grandeur : stockage d'ovocytes ou d'embryons souvent de quelques centaines à environ 1 000 $/an ; stockage de sperme souvent quelques centaines de dollars par an.\n\nUn bon état d'esprit : traite le stockage comme un abonnement que tu suis activement. La plupart des soucis rencontrés sont administratifs, pas médicaux.",
              "bulletPoints": [
                "📌 Des frais de dossier ou d'admin peuvent s'ajouter au stockage.",
                "⚠️ Des pénalités de retard ou de réactivation en cas de paiement raté.",
                "📦 Des frais de retrait quand tu es prête à utiliser ou déplacer les échantillons.",
                "🚚 Des frais de transfert et d'expédition si tu déplaces les échantillons plus tard."
              ]
            },
            {
              "title": "L'administratif compte plus qu'on ne le pense",
              "subtitle": "Consentements, décisions, devenir des échantillons",
              "content": "Après la préservation, la tâche administrative la plus importante, c'est de s'assurer que tes contrats de stockage et tes consentements collent à ta vie. Ce n'est pas la partie fun, mais ça te protège.\n\nSi tu ne fais qu'une seule chose après cet article : télécharge tes consentements signés et range-les dans un endroit sûr.",
              "bulletPoints": [
                "Qui peut décider : si tu as stocké des ovocytes ou du sperme, tu contrôles en général les décisions. Pour des embryons créés avec les gamètes d'un·e partenaire, les décisions sont souvent partagées sauf mention contraire dans les documents.",
                "Ce qui se passe si tu te sépares d'un·e partenaire, si quelqu'un décède ou perd la capacité de décider, si la clinique ne peut plus te joindre, ou si les frais restent impayés longtemps.",
                "Options de devenir des embryons : maintien du stockage, destruction, don à la recherche (là où c'est permis) ou don à une autre personne ou famille.",
                "Beaucoup ne veulent pas trancher tout de suite le devenir, c'est normal. Mais il vaut la peine de comprendre ce que tu as signé."
              ]
            },
            {
              "title": "Comment garder le stockage sûr dans la durée",
              "subtitle": "Des questions à poser sans être experte",
              "content": "Tu n'as pas besoin de connaître l'ingénierie des labos pour poser de bonnes questions. Si tu choisis un site de stockage ou que tu évalues le tien, cherche ceci. Si un site ne peut pas expliquer clairement sa traçabilité et sa surveillance, c'est un vrai signal pour marquer une pause.",
              "bulletPoints": [
                "🔐 Traçabilité et vérification d'identité claires : comment évitent-ils les erreurs d'échantillons et vérifient-ils qui peut autoriser des changements ?",
                "🔔 Surveillance continue et alarmes : comment les cuves sont-elles surveillées, et que se passe-t-il en cas d'alarme ?",
                "✅ Normes de qualité et accréditation : beaucoup de labos suivent des accréditations reconnues et les recommandations de bonnes pratiques pour la cryoconservation."
              ]
            },
            {
              "title": "Est-ce que je peux déplacer mes échantillons plus tard ?",
              "subtitle": "Transferts entre cliniques ou sites",
              "content": "Oui. Ça se fait tout le temps : déménagement, changement de clinique, choix d'un autre labo, ou passage à un stockage long terme moins cher. Tu signes une autorisation de transfert, le site receveur donne les consignes d'acceptation, et l'expédition se fait avec un transport cryogénique spécialisé.\n\nUn transfert demande de la coordination : mieux vaut ne pas s'y prendre au dernier moment (juste avant un cycle de transfert, par exemple).",
              "bulletPoints": [
                "💰 Demande aux deux sites le coût total (paperasse + envoi).",
                "🔗 Confirme comment ils vérifient l'identité et la chaîne de contrôle pendant le déplacement.",
                "📅 Fais-toi préciser des délais réalistes entre « demande envoyée » et « échantillons reçus »."
              ]
            },
            {
              "title": "Penser à la suite",
              "subtitle": "À quoi peut ressembler « les utiliser »",
              "content": "La préservation garde les options ouvertes. Elle n'impose pas un chemin unique. Un cadrage utile : la décision, ce n'est pas « je les utilise ou pas ? » C'est souvent « À quel moment je veux avoir cette option disponible, et qu'est-ce qui me ferait me sentir prête à essayer ? »",
              "bulletPoints": [
                "Le sperme congelé peut ensuite être utilisé en IIU (selon la qualité après décongélation) ou en FIV/ICSI (souvent préféré quand les paillettes sont peu nombreuses).",
                "Les ovocytes congelés sont ensuite décongelés, fécondés au labo (l'ICSI est couramment utilisée), amenés au stade embryon et transférés dans un cycle planifié.",
                "Les embryons congelés : l'étape suivante est en général un transfert d'embryon congelé quand tu es prête."
              ]
            },
            {
              "title": "Un point annuel très simple",
              "subtitle": "10 minutes, une fois par an",
              "content": "Une fois par an (souvent au moment de la facture), fais cette petite revue. Cette mini-routine évite les prises de tête les plus courantes.",
              "bulletPoints": [
                "📞 Vérifie que le site a bien tes email, téléphone et adresse à jour.",
                "💳 Confirme la fréquence de facturation et ce qui se passe en cas d'impayé.",
                "📜 Relis le résumé de tes consentements (surtout pour les embryons).",
                "💾 Enregistre ton reçu ou facture et la dernière copie de consentement.",
                "🔄 Si ta vie a changé (mariage, séparation, déménagement, changement de santé), demande si les documents doivent être mis à jour."
              ]
            },
            {
              "title": "Quand chercher un soutien supplémentaire",
              "subtitle": "Tu n'as pas à tout décider aujourd'hui",
              "content": "Les décisions autour d'un stockage long terme peuvent devenir étonnamment émotionnelles, surtout pour les embryons. Si tu te sens bloquée, dans l'évitement ou débordée, tu n'es pas seule. L'objectif : garder tes options sûres, claires et alignées avec ta vie à mesure qu'elle change.",
              "bulletPoints": [
                "🧠 Une séance avec un·e conseiller·ère formé·e à la fertilité.",
                "🏥 Une consultation en clinique centrée sur la construction familiale.",
                "💲 Un appel avec un·e conseiller·ère financier·ère si les coûts s'accumulent et que tu veux voir des alternatives."
              ]
            }
          ]
        }
      }
    },
    {
      "id": "new-article-diet-and-fertility",
      "locale": "fr",
      "variant": {
        "title": "Alimentation et fertilité",
        "subtitle": "Comment l'alimentation peut influencer la santé reproductive",
        "body": {
          "introduction": "L'alimentation, parmi de nombreux autres facteurs, peut influencer la santé reproductive, avec des recherches qui montrent un lien entre alimentation et fertilité. Certains aliments et nutriments sont considérés comme bénéfiques pour la santé reproductive, tandis que d'autres peuvent avoir un impact négatif. Cet article explique comment l'alimentation peut influencer la fertilité et propose des choix nutritionnels qui peuvent la soutenir.",
          "quickSummary": [
            {
              "title": "Le régime méditerranéen aide.",
              "description": "Suivre le régime méditerranéen peut augmenter la probabilité de succès en FIV en soutenant la santé reproductive."
            },
            {
              "title": "Choisis les bonnes graisses.",
              "description": "Une alimentation riche en graisses insaturées, céréales complètes, légumes et poissons peut améliorer la fertilité chez les femmes comme chez les hommes."
            },
            {
              "title": "Limite les aliments transformés.",
              "description": "Les graisses saturées et les sucres peuvent avoir un impact négatif sur la fertilité."
            }
          ],
          "sections": [
            {
              "title": "Bonnes graisses et céréales complètes",
              "subtitle": "Les fondations",
              "content": "Les données suggèrent qu'une alimentation riche en graisses insaturées, céréales complètes, légumes et poissons peut améliorer la fertilité chez les femmes comme chez les hommes. À l'inverse, graisses saturées et sucres sont associés à des résultats moins favorables."
            },
            {
              "title": "Les régimes anti-inflammatoires",
              "subtitle": "Pourquoi l'inflammation compte",
              "content": "La recherche indique que les régimes anti-inflammatoires, comme le régime méditerranéen, peuvent améliorer la fertilité, augmenter les taux de succès en PMA et améliorer la qualité du sperme. Ces régimes favorisent les acides gras mono-insaturés et polyinsaturés oméga-3, les flavonoïdes, et limitent les viandes rouges et transformées."
            },
            {
              "title": "Le régime méditerranéen",
              "subtitle": "Une voie possible vers la fertilité",
              "content": "Ce régime a de nombreux atouts santé, comme une meilleure santé cardiaque et une meilleure gestion du poids. La recherche suggère aussi un impact positif sur la fertilité grâce à ses propriétés anti-inflammatoires et antioxydantes, qui peuvent bénéficier à la santé reproductive des deux partenaires. Ce n'est pas seulement un régime, c'est un mode de vie qui inclut aussi de l'activité physique et des repas partagés."
            },
            {
              "title": "Les composantes clés du régime méditerranéen",
              "subtitle": "Ce qu'on met dans l'assiette",
              "content": "Les composantes principales :",
              "bulletPoints": [
                "Beaucoup de fruits, légumes et céréales complètes.",
                "Des bonnes graisses surtout via l'huile d'olive, les noix et les graines.",
                "Poissons, fruits de mer, volaille et produits laitiers avec modération.",
                "Viandes rouges et aliments transformés en petite quantité.",
                "Herbes et épices pour parfumer, en réduisant le sel.",
                "Vin rouge optionnel, avec modération."
              ]
            },
            {
              "title": "Faire des choix éclairés",
              "subtitle": "Ta trajectoire",
              "content": "Le chemin vers la parentalité peut demander des choix alimentaires qui soutiennent la fertilité. Comprendre le lien entre alimentation et fertilité permet de prendre des décisions nutritionnelles informées. Que ce soit un supplément comme l'acide folique, ou l'essai d'un régime méditerranéen ou anti-inflammatoire, ces approches peuvent s'inscrire dans une stratégie globale pour soutenir la fertilité. Un accompagnement personnalisé par un·e professionnel·le de santé peut vraiment aider."
            }
          ]
        }
      }
    },
    {
      "id": "new-article-temperature-and-fertility",
      "locale": "fr",
      "variant": {
        "title": "Chaleur, température et fertilité masculine",
        "subtitle": "Comment la chaleur affecte le sperme et ce que tu peux faire",
        "body": {
          "introduction": "On ne pense pas souvent à la température comme à un facteur de fertilité, jusqu'à ce qu'un·e clinicien·ne le dise : la production de sperme fonctionne mieux quand les testicules restent un peu plus frais que le reste du corps. Ce n'est pas une anecdote, c'est une vraie donnée de biologie masculine. Quand l'exposition à la chaleur est assez fréquente ou intense (mode de vie, travail, maladie, environnement), elle peut réduire temporairement la qualité du sperme et, dans certains cas, rendre la conception plus difficile.",
          "quickSummary": [
            {
              "title": "Le refroidissement compte.",
              "description": "La production de sperme fonctionne mieux quand les testicules restent quelques degrés en dessous de la température corporelle."
            },
            {
              "title": "Les effets sont réversibles.",
              "description": "Les variations liées à la chaleur récupèrent souvent après avoir réduit l'exposition, mais l'amélioration prend 2 à 3 mois à se voir."
            },
            {
              "title": "De petits gestes aident.",
              "description": "Éviter les jacuzzis, ne pas poser l'ordinateur portable sur les cuisses, et choisir des sous-vêtements respirants font une vraie différence."
            }
          ],
          "sections": [
            {
              "title": "Pourquoi la température compte pour le sperme",
              "subtitle": "La biologie de la spermatogenèse",
              "content": "Le sperme est produit dans les testicules par un processus appelé spermatogenèse. Pour bien tourner, les testicules ont besoin d'être quelques degrés plus frais que la température corporelle centrale. C'est pour ça qu'ils sont à l'extérieur du corps et que le scrotum a ses propres mécanismes de refroidissement.",
              "bulletPoints": [
                "Quand les testicules passent trop de temps au-dessus de leur zone de confort, les cellules productrices de sperme sont stressées.",
                "Les études associent le stress thermique à une baisse de la concentration et du nombre total de spermatozoïdes.",
                "La chaleur peut réduire la mobilité (comment ils bougent) et affecter la morphologie (leur forme).",
                "Dans certains contextes, la chaleur augmente les marqueurs de stress ADN/emballage du sperme.",
                "Important : les effets de la chaleur sont souvent réversibles, mais ça prend du temps."
              ]
            },
            {
              "title": "La chronologie qui aide à tout comprendre",
              "subtitle": "Pourquoi les changements prennent des mois",
              "content": "Les spermatozoïdes mettent environ 2 à 3 mois à se développer et à mûrir. Ce qui veut dire :",
              "bulletPoints": [
                "L'exposition à la chaleur aujourd'hui peut apparaître dans un spermogramme des semaines plus tard.",
                "Si tu réduis l'exposition à la chaleur maintenant, l'amélioration (si elle a lieu) se voit souvent après deux ou trois mois, pas quelques jours.",
                "C'est aussi pour ça que les clinicien·nes refont souvent un spermogramme après ~3 mois quand tu changes d'hygiène de vie."
              ]
            },
            {
              "title": "À quoi ressemble concrètement l'exposition à la chaleur",
              "subtitle": "Exemples de la vraie vie",
              "content": "La plupart des chaleurs du quotidien ne posent pas problème : le corps régule bien la température scrotale. Les soucis viennent d'expositions répétées, prolongées ou très intenses.",
              "bulletPoints": [
                "Jacuzzis, bains chauds, sauna fréquent : une exposition régulière à forte chaleur peut suffisamment élever la température scrotale pour affecter les paramètres du sperme.",
                "Fièvre et maladie : une fièvre élevée est une forme de stress thermique global qui peut affecter la qualité du sperme des semaines plus tard.",
                "Chaleur au travail et équipements de protection : les métiers avec exposition prolongée à la chaleur peuvent augmenter le risque d'infertilité. Les vêtements de protection lourds peuvent compliquer le refroidissement.",
                "Pièges thermiques du quotidien : longues heures assis cuisses serrées, ordinateur portable directement sur les cuisses, sous-vêtements ou pantalons très serrés qui réduisent la ventilation."
              ]
            },
            {
              "title": "À quel point la chaleur affecte-t-elle la fertilité ?",
              "subtitle": "L'impact réel",
              "content": "La réponse honnête : ça dépend. De larges corpus de recherche suggèrent que des températures ambiantes plus élevées et les vagues de chaleur sont associées à de moins bons paramètres de sperme au niveau populationnel.",
              "bulletPoints": [
                "Si tu es déjà proche de la limite basse des paramètres normaux, la chaleur peut « pousser » les choses dans une zone où concevoir devient plus difficile.",
                "Si tes paramètres de base sont solides, la chaleur ne modifiera peut-être pas ta capacité à concevoir, mais elle peut réduire la marge.",
                "L'orientation générale de la recherche est assez cohérente pour que la température soit désormais considérée comme un facteur environnemental réel pour la santé reproductive masculine."
              ]
            },
            {
              "title": "Ce que tu peux faire",
              "subtitle": "Gestes simples et pratiques",
              "content": "Si tu essaies de concevoir (ou que tu prépares un bilan ou un traitement), ces gestes sont raisonnables et à faible risque :",
              "bulletPoints": [
                "Mets en pause jacuzzis, bains très chauds et sauna pendant quelques mois quand tu es en essais.",
                "Si tu travailles dans la chaleur, demande ce qui est possible : plus de pauses, hydratation, options de refroidissement, ou ajustement de l'équipement de protection quand c'est sûr.",
                "Garde l'ordinateur portable hors des cuisses (bureau ou lap desk).",
                "Choisis sous-vêtements et pantalons qui ne retiennent pas la chaleur (confort + ventilation).",
                "Si tu as eu récemment une forte fièvre, sache que ça peut affecter le spermogramme un moment. Ton·ta clinicien·ne peut proposer un retest plus tard."
              ]
            },
            {
              "title": "La température affecte-t-elle aussi la fertilité des femmes ?",
              "subtitle": "Une recherche plus large",
              "content": "Il y a une recherche croissante sur la température et la reproduction en général. De grandes études associent la chaleur extrême (et parfois le froid) à des changements dans la fécondabilité du couple (temps pour tomber enceinte) et à des résultats en PMA, même si les preuves sont plus variables et les mécanismes différents de ceux de la chaleur chez les hommes."
            },
            {
              "title": "Quand consulter",
              "subtitle": "Signes qu'il est temps de faire le point",
              "content": "Envisage un rendez-vous si :",
              "bulletPoints": [
                "Vous essayez depuis la durée recommandée pour ton âge et ta situation.",
                "Tu as une exposition à la chaleur importante connue (travail, sauna ou jacuzzi fréquents).",
                "Tu as des symptômes comme une douleur ou un gonflement testiculaire (qui peuvent signaler d'autres problèmes à évaluer).",
                "Tu vas commencer une PMA et tu veux optimiser la qualité du sperme.",
                "Un·e clinicien·ne peut aussi t'aider à voir si la chaleur est probablement un facteur majeur ou juste une petite pièce du puzzle."
              ]
            }
          ]
        }
      }
    },
    {
      "id": "new-article-stis-and-fertility",
      "locale": "fr",
      "variant": {
        "title": "IST et fertilité",
        "subtitle": "Ce qu'il faut savoir sur les infections quand on essaie de concevoir",
        "body": {
          "introduction": "Les IST (infections sexuellement transmissibles) sont fréquentes, et beaucoup ne donnent aucun symptôme : c'est pour ça qu'elles peuvent affecter la fertilité sans qu'on s'en rende compte. La bonne nouvelle : la plupart se traitent, et agir tôt protège tes options futures.",
          "quickSummary": [
            {
              "title": "Un impact silencieux.",
              "description": "Beaucoup d'IST ne donnent aucun symptôme mais peuvent affecter la fertilité si elles ne sont pas traitées."
            },
            {
              "title": "L'inflammation est la clé.",
              "description": "La plupart des problèmes de fertilité liés aux IST viennent d'une inflammation et de cicatrices dans l'appareil reproducteur."
            },
            {
              "title": "Le dépistage te protège.",
              "description": "Comme les symptômes sont souvent absents, le dépistage régulier est le meilleur moyen d'attraper les infections tôt et de les traiter."
            }
          ],
          "sections": [
            {
              "title": "La vue d'ensemble : comment les IST peuvent affecter la fertilité",
              "subtitle": "Comprendre le lien",
              "content": "La plupart des problèmes de fertilité liés aux IST viennent de l'inflammation.",
              "bulletPoints": [
                "Chez les personnes avec un utérus, certaines infections peuvent monter depuis le col dans l'utérus et les trompes, provoquant une maladie inflammatoire pelvienne (MIP).",
                "La MIP peut laisser des cicatrices autour des trompes, ce qui peut compliquer la conception et augmenter le risque de grossesse extra-utérine.",
                "Chez les personnes avec des testicules, les infections peuvent enflammer certaines parties de l'appareil reproducteur, comme l'épididyme (la structure qui stocke et transporte le sperme).",
                "L'épididymite se traite en général bien, mais si elle est sévère ou récidivante, inflammation et cicatrices peuvent parfois contribuer à des problèmes de fertilité.",
                "Le message clé : les IST affectent surtout la fertilité quand elles restent non traitées assez longtemps pour causer une inflammation ou des cicatrices plus profondes."
              ]
            },
            {
              "title": "Les IST qui comptent le plus pour la fertilité",
              "subtitle": "Celles à connaître",
              "content": "Si l'objectif est de protéger la fertilité, voici les infections à connaître, pas pour te faire peur, mais pour concentrer l'attention là où le traitement précoce compte le plus.",
              "bulletPoints": [
                "Chlamydia est en tête des préoccupations parce qu'elle est très fréquente et souvent asymptomatique. Non traitée, elle peut contribuer à une MIP et à des cicatrices tubaires chez les femmes. Chez les hommes, elle peut causer une urétrite et une épididymite.",
                "Gonorrhée suit un schéma proche. Elle peut être asymptomatique, mais peut aussi causer une inflammation plus intense menant à une MIP chez les femmes et une urétrite/épididymite chez les hommes.",
                "Mycoplasma genitalium (Mgen) est moins connu et n'est pas toujours inclus dans les panels IST de base. Il a été associé à une cervicite, à une MIP et à des complications liées à la fertilité chez les femmes.",
                "Trichomonase (« trich ») est fréquente et se traite. Elle peut causer une inflammation génitale et est associée à des complications de grossesse comme l'accouchement prématuré."
              ]
            },
            {
              "title": "Infections virales et systémiques",
              "subtitle": "Importantes pour planifier une grossesse",
              "content": "Ces infections comptent pour la planification d'une grossesse même si elles ne causent pas en général d'infertilité par cicatrisation :",
              "bulletPoints": [
                "HPV ne cause pas directement d'infertilité, mais certaines lésions cervicales liées au HPV et leurs traitements peuvent affecter la planification d'une grossesse. La vaccination HPV aide à prévenir cancers et pré-cancers.",
                "Herpès (HSV) n'empêche en général pas de concevoir, mais compte en grossesse car une infection près de l'accouchement peut présenter un risque pour le nouveau-né.",
                "Syphilis est critique à identifier et traiter en grossesse car elle peut avoir des conséquences graves pour le bébé si elle n'est pas traitée.",
                "VIH n'empêche pas automatiquement une grossesse. Avec un traitement efficace et une suppression virale, la grossesse peut être très sûre et le risque de transmission au bébé peut être extrêmement bas."
              ]
            },
            {
              "title": "« Mais je me sens bien. » Pourquoi le dépistage compte",
              "subtitle": "La face cachée des IST",
              "content": "Beaucoup pensent qu'ils « sauraient » s'ils avaient une IST. Malheureusement, c'est souvent faux, surtout avec la chlamydia. Tu peux te sentir parfaitement normale pendant qu'une infection provoque une inflammation en silence.",
              "bulletPoints": [
                "C'est pour ça que des recommandations de dépistage existent : dépister attrape les infections avant qu'elles ne fassent des dégâts durables.",
                "Aux États-Unis, un dépistage régulier de la chlamydia et de la gonorrhée est fortement recommandé pour les femmes sexuellement actives de moins de 25 ans et d'autres personnes à risque augmenté.",
                "Le dépistage est simple, souvent juste un test d'urine ou un prélèvement, et le traitement est en général une simple antibiothérapie."
              ]
            },
            {
              "title": "Si tu essaies de concevoir",
              "subtitle": "Une approche pratique",
              "content": "Si la grossesse est dans tes plans, une approche calme et pratique est la meilleure :",
              "bulletPoints": [
                "Commence par un dépistage tôt, surtout si tu n'as pas été dépistée récemment ou si tu as eu des partenaires nouveaux ou multiples.",
                "Si un test est positif, traite rapidement et fais aussi traiter le·s partenaire·s : la réinfection est fréquente quand une seule personne est traitée.",
                "Beaucoup de recommandations conseillent un contrôle quelques mois après le traitement de la chlamydia ou de la gonorrhée, parce qu'une nouvelle infection peut survenir même si le traitement a marché.",
                "Si tu as déjà eu une MIP, des IST à répétition, des douleurs pelviennes ou une grossesse extra-utérine, dis-le à ton·ta clinicien·ne : ces éléments peuvent changer les examens de fertilité à prioriser."
              ]
            },
            {
              "title": "Quand consulter en urgence",
              "subtitle": "Signes d'alerte",
              "content": "Consulte rapidement si tu as :",
              "bulletPoints": [
                "Une douleur pelvienne ou du bas-ventre sévère.",
                "De la fièvre avec des symptômes pelviens.",
                "Des pertes inhabituelles avec douleur ou fièvre.",
                "Des douleurs pendant les rapports associées à d'autres symptômes.",
                "Ce peuvent être les signes d'une MIP ou d'une autre infection à traiter rapidement."
              ]
            }
          ]
        }
      }
    }
  ]
}
